/**
 * YasakKarti.jsx — denetmen kişi sayfasının (PersonelDetayPage) Kontroller sekmesindeki "Giriş Yasağı" kartı.
 * Backlog #368 (bundle): Kişi Kartı Bilgileri kartından (KisiKartBilgileriKarti.jsx, #285) ayrılan "Girişi yasaklı"
 * anahtarı + notu + onay penceresi; metinler ve davranış AYNI. (Eski kartın Yelek anahtarı KKD'ye taşındı: KkdKarti.)
 *
 * Yasak KİŞİ düzeyindedir (her fuar); STAFF'a özel PATCH ile yazılır (backend #286, `setPersonYasak`):
 * koyarken not ZORUNLU ve en çok 500 karakter, kaldırırken not silinir. Yasaklıyken Kart Yazdır yerine YASAKLI yazar
 * ve kart basılamaz; kişi sayfasının üst bilgisi, Kişi Listesi ve kart kararı yazma sonrası `onChanged` ile tazelenir.
 * Yazma hatası `onError(mesaj)`; pencere hatada kapanır.
 *
 * @param {object}   person     Kişi nesnesi (`detay.kisi`): name, surname, forbidden, yasak { yasakli, not, tarih, degistirenAdi }
 * @param {Function} onChanged  `(mesaj)` — yazma başarılı
 * @param {Function} onError    `(mesaj)` — yazma başarısız
 */
import React, { useState } from "react";
import {
  Badge, Button, Card, CardBody, CardHeader, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner,
} from "reactstrap";
import { Ban } from "lucide-react";
import { setPersonYasak, isStaffRefusal } from "../../../services/kisiAdminService";
import { currentLocaleTag } from "../../../i18n";

const NOT_UZUNLUK = 500;

function tarihSaat(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

function hataMetni(e) {
  if (isStaffRefusal(e)) return "Bu işlem yalnızca SAKA personeli içindir.";
  if (e?.status == null) return "Sunucu yanıt vermedi.";
  return e?.message || "İşlem başarısız oldu.";
}

export default function YasakKarti({ person, onChanged, onError }) {
  const [pencere, setPencere] = useState(false);
  const [not, setNot] = useState("");
  const [isleniyor, setIsleniyor] = useState(false);

  const personId = person?.personId ?? null;
  const adSoyad = `${person?.name ?? ""} ${person?.surname ?? ""}`.trim() || "Bu kişi";
  const yasak = person?.yasak ?? null; // yalnızca denetmen yanıtında dolu
  const yasakli = !!(person?.forbidden ?? yasak?.yasakli);
  const sonDegisiklik = [yasak?.degistirenAdi, tarihSaat(yasak?.tarih)].filter(Boolean).join(" · ");

  function pencereyiAc() {
    setNot("");
    setPencere(true);
  }

  async function kaydet() {
    const hedef = !yasakli;
    setIsleniyor(true);
    try {
      await setPersonYasak(personId, { yasakli: hedef, not });
      setPencere(false);
      onChanged?.(hedef ? `${adSoyad} için giriş yasağı konuldu.` : `${adSoyad} için giriş yasağı kaldırıldı.`);
    } catch (e) {
      setPencere(false);
      onError?.(hataMetni(e));
    } finally {
      setIsleniyor(false);
    }
  }

  const notGecerli = yasakli || (not.trim().length > 0 && not.trim().length <= NOT_UZUNLUK);

  return (
    <Card className="h-100 mb-0">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <Ban size={16} className="text-muted" />
        <span className="fw-semibold">Giriş Yasağı</span>
      </CardHeader>
      <CardBody>
        <FormGroup switch className="mb-0">
          <Input
            type="switch" id="kisi-yasak-karti" checked={yasakli} disabled={isleniyor || personId == null}
            onChange={pencereyiAc}
          />
          <Label check for="kisi-yasak-karti" className="fw-semibold d-inline-flex align-items-center gap-1">
            {yasakli ? <><Ban size={14} className="text-danger" />Girişi yasaklı</> : "Girişi yasaklı değil"}
          </Label>
        </FormGroup>
        {yasakli && (
          <div className="small mt-1">
            <Badge color="danger" className="fw-normal me-1">Not</Badge>
            {yasak?.not ? yasak.not : <span className="text-muted">Not kaydı yok</span>}
          </div>
        )}
        {sonDegisiklik && <div className="small text-muted mt-1">Son değişiklik: {sonDegisiklik}</div>}
      </CardBody>

      <Modal isOpen={pencere} toggle={() => !isleniyor && setPencere(false)}>
        <ModalHeader toggle={() => !isleniyor && setPencere(false)}>
          {yasakli ? "Giriş yasağı kaldırılsın mı?" : "Giriş yasağı konulsun mu?"}
        </ModalHeader>
        <ModalBody className="small">
          {yasakli ? (
            <p className="mb-0">
              <strong>{adSoyad}</strong> için giriş yasağı kaldırılacak ve yasak notu silinecek. Kişi, belgeleri tamamsa yeniden
              kart alabilir.
            </p>
          ) : (
            <>
              <p>
                <strong>{adSoyad}</strong> her fuarda <strong>Girişi Yasaklı</strong> sayılır: Kart Yazdır yerine YASAKLI
                yazar ve kart basılamaz.
              </p>
              <Label className="small mb-1">Yasak notu *</Label>
              <Input
                type="textarea" rows={3} maxLength={NOT_UZUNLUK} value={not} onChange={(e) => setNot(e.target.value)}
                placeholder="Yasağın nedeni"
              />
              <div className="form-text">{not.length}/{NOT_UZUNLUK}</div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPencere(false)} disabled={isleniyor}>Vazgeç</Button>
          <Button color={yasakli ? "primary" : "danger"} onClick={kaydet} disabled={isleniyor || !notGecerli}>
            {isleniyor ? <Spinner size="sm" /> : yasakli ? "Yasağı kaldır" : "Yasak koy"}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
